import { DayRow } from "../types/CostCenterTypes";

interface ValidationResult {
    overlaps: Map<string, Set<string>>;
    warnings: Map<string, string[]>;
}

const toMin = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};

const rangeDuration = (start: string, end: string): number => {
    const s = toMin(start);
    const e = toMin(end);
    return e >= s ? e - s : 1440 - s + e; 
};

const rangesOverlap = (
    s1: string, e1: string,
    s2: string, e2: string,
): boolean => {
    const toInterval = (start: string, end: string): [number, number] => {
        const s = toMin(start);
        const e = toMin(end);
        return e >= s ? [s, e] : [s, e + 1440];
    };

    const [a1, b1] = toInterval(s1, e1);
    const [a2, b2] = toInterval(s2, e2);

    const overlaps = (ax: number, bx: number, ay: number, by: number) =>
        ax < by && ay < bx;

    return (
        overlaps(a1, b1, a2, b2) ||
        overlaps(a1, b1, a2 - 1440, b2 - 1440) ||
        overlaps(a1, b1, a2 + 1440, b2 + 1440)
    );
};

const isContainedInShift = (
    as: string, ae: string,
    ss: string, se: string,
): boolean => {
    const toNorm = (start: string, end: string): [number, number] => {
        const s = toMin(start);
        const e = toMin(end);
        return e >= s ? [s, e] : [s, e + 1440];
    };

    const [ss2, se2] = toNorm(ss, se);

    const check = (offset: number) => {
        const as2 = toMin(as) + offset;
        const ae2 = toMin(ae) + offset;
        const [as3, ae3] = ae2 >= as2 ? [as2, ae2] : [as2, ae2 + 1440];
        return as3 >= ss2 && ae3 <= se2;
    };

    return check(0) || check(1440) || check(-1440);
};

const validateDayRows = (dayRows: DayRow[]): ValidationResult => {
    const overlaps = new Map<string, Set<string>>();
    const warnings = new Map<string, string[]>();

    const addWarning = (rowId: string, msg: string) => {
        if (!warnings.has(rowId)) warnings.set(rowId, []);
        warnings.get(rowId)!.push(msg);
    };

    dayRows.forEach((row) => {
        const valid = row.assignments.filter(a => a.costCenterId !== -1);

        for (let i = 0; i < valid.length; i++) {
            for (let j = i + 1; j < valid.length; j++) {
                const a = valid[i];
                const b = valid[j];
                if (rangesOverlap(a.startTime, a.endTime, b.startTime, b.endTime)) {
                    if (!overlaps.has(row.id)) overlaps.set(row.id, new Set());
                    overlaps.get(row.id)!.add(a.id);
                    overlaps.get(row.id)!.add(b.id);
                }
            }
        }

        if (row.shifts.length === 0 || valid.length === 0) return;

        row.shifts.forEach((shift) => {
            const shiftDur = rangeDuration(shift.startTime, shift.endTime);
            const assigned = valid
                .filter(a => {
                    if (a.shiftNumber !== undefined) return a.shiftNumber === shift.shiftNumber;
                    return isContainedInShift(a.startTime, a.endTime, shift.startTime, shift.endTime);
                })
                .reduce((acc, a) => acc + rangeDuration(a.startTime, a.endTime), 0);

            if (assigned > 0 && assigned < shiftDur) {
                const diff = shiftDur - assigned;
                const h = Math.floor(diff / 60);
                const m = diff % 60;
                addWarning(
                    row.id,
                    `Turno ${shift.shiftNumber} (${shift.startTime}-${shift.endTime}): la distribución cubre ${assigned} min pero el turno dura ${shiftDur} min (faltan ${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'm' : ''}).`
                );
            }
        });

        valid.forEach(a => {
            const belongsToAShift = row.shifts.some(shift => {
                if (a.shiftNumber !== undefined) return a.shiftNumber === shift.shiftNumber;
                return isContainedInShift(a.startTime, a.endTime, shift.startTime, shift.endTime);
            });
            if (!belongsToAShift) {
                addWarning(
                    row.id,
                    `El rango ${a.startTime}-${a.endTime} está fuera de los turnos del día.`
                );
            }
        });
    });

    return { overlaps, warnings };
};

export { validateDayRows };